import React, { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ArrowLeft, CreditCard, Upload, FileText, Download, Copy } from "lucide-react";
import { motion } from "framer-motion";

export default function BancoDoBrasilRJX() {
  // Estados da Validação (Parte 1)
  const [arquivoBruto, setArquivoBruto] = useState(null);
  const [arquivoEditado, setArquivoEditado] = useState(null);
  const [divergencias, setDivergencias] = useState([]);
  const [validacaoCompleta, setValidacaoCompleta] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [arquivoCorrigido, setArquivoCorrigido] = useState(null);
  
  // Estados do Processamento (Parte 2)
  const [csvData, setCsvData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const fileInputBrutoRef = useRef(null);
  const fileInputEditadoRef = useRef(null);
  const fileInputRef = useRef(null);

  // ==================== PARTE 1: VALIDAÇÃO ====================
  
  // Função para converter valor do formato bruto (centavos) para decimal
  const converterValorBruto = (valorStr) => {
    // Remove zeros à esquerda e converte: 00000000001600000 -> 16000.00
    const valorNumerico = parseInt(valorStr, 10);
    return valorNumerico / 100; // Converte centavos para reais
  };

  // Função para validar e comparar arquivos
  const validarArquivos = (contentBruto, contentEditado) => {
    console.log('🔍 Iniciando validação de arquivos...');
    
    const linhasBruto = contentBruto.split('\n').filter(line => line.trim());
    const linhasEditado = contentEditado.split('\n').filter(line => line.trim());
    
    const divergenciasEncontradas = [];
    let arquivoCorrigidoLinhas = [];

    linhasBruto.forEach((linhaBruto, index) => {
      const colunasBruto = linhaBruto.split(';');
      
      if (index >= linhasEditado.length) {
        console.log(`⚠️ Linha ${index + 1} não existe no arquivo editado`);
        return;
      }
      
      const linhaEditada = linhasEditado[index];
      const colunasEditado = linhaEditada.split(';');
      
      if (colunasBruto.length < 11 || colunasEditado.length < 11) {
        arquivoCorrigidoLinhas.push(linhaEditada);
        return;
      }

      // Extrair dados relevantes
      const dataBruto3 = colunasBruto[3]?.trim() || '';
      const dataBruto4 = colunasBruto[4]?.trim() || '';
      const historico = colunasBruto[9]?.trim();
      const valorBruto = converterValorBruto(colunasBruto[10]?.trim() || '0');
      
      // Extrair valor do arquivo editado (pode estar em vários formatos)
      let valorEditadoStr = colunasEditado[10]?.trim() || '0';
      valorEditadoStr = valorEditadoStr.replace('R$', '').trim();
      const valorEditado = parseFloat(valorEditadoStr.replace(/\./g, '').replace(',', '.'));

      // Verificar se datas precisam de correção (devem ter 8 dígitos)
      const dataEditado3 = colunasEditado[3]?.trim() || '';
      const dataEditado4 = colunasEditado[4]?.trim() || '';
      const dataCorreta3 = dataBruto3.length === 8 && dataEditado3.length !== 8;
      const dataCorreta4 = dataBruto4.length === 8 && dataEditado4.length !== 8;

      // Comparar valores com tolerância de 0.01 centavos
      const diferenca = Math.abs(valorBruto - valorEditado);
      const temDivergenciaValor = diferenca > 0.01;
      const temDivergenciaData = dataCorreta3 || dataCorreta4;
      
      if (temDivergenciaValor) {
        divergenciasEncontradas.push({
          linha: index + 1,
          data: dataBruto3,
          historico: historico,
          valorBruto: valorBruto,
          valorEditado: valorEditado,
          diferenca: valorEditado - valorBruto
        });
        
        console.log(`❌ DIVERGÊNCIA Linha ${index + 1}: ${historico} | Bruto: R$ ${valorBruto.toFixed(2)} | Editado: R$ ${valorEditado.toFixed(2)}`);
      }
      
      if (temDivergenciaValor || temDivergenciaData) {
        const colunaCorrigida = [...colunasEditado];
        
        // Corrigir datas se necessário
        if (dataCorreta3) {
          colunaCorrigida[3] = dataBruto3;
          console.log(`📅 Corrigindo data col.3 Linha ${index + 1}: ${dataEditado3} → ${dataBruto3}`);
        }
        if (dataCorreta4) {
          colunaCorrigida[4] = dataBruto4;
          console.log(`📅 Corrigindo data col.4 Linha ${index + 1}: ${dataEditado4} → ${dataBruto4}`);
        }
        
        // Corrigir valor se necessário
        if (temDivergenciaValor) {
          // Formatar valor: separar milhares com ponto e decimais com vírgula (ex: R$ 1.000,00 ou R$ 12,80)
          let valorFormatado = valorBruto.toFixed(2);
          const [inteiro, decimal] = valorFormatado.split('.');
          
          // Adicionar separador de milhares (ponto)
          const inteiroFormatado = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
          
          // Montar valor final com vírgula para decimais
          const novoValor = ` R$ ${inteiroFormatado},${decimal} `;
          colunaCorrigida[10] = novoValor;
        }
        
        arquivoCorrigidoLinhas.push(colunaCorrigida.join(';'));
      } else {
        // Valor e datas corretos, manter linha original
        arquivoCorrigidoLinhas.push(linhaEditada);
      }
    });

    console.log(`\n📊 Total de divergências encontradas: ${divergenciasEncontradas.length}`);
    
    return {
      divergencias: divergenciasEncontradas,
      conteudoCorrigido: arquivoCorrigidoLinhas.join('\n')
    };
  };

  // Handler para upload do arquivo bruto
  const handleArquivoBruto = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setArquivoBruto({
        nome: file.name,
        conteudo: e.target.result
      });
      console.log('✅ Arquivo bruto carregado:', file.name);
    };
    reader.readAsText(file);
  };

  // Handler para upload do arquivo editado
  const handleArquivoEditado = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setArquivoEditado({
        nome: file.name,
        conteudo: e.target.result
      });
      console.log('✅ Arquivo editado carregado:', file.name);
    };
    reader.readAsText(file);
  };

  // Executar validação
  const executarValidacao = () => {
    if (!arquivoBruto || !arquivoEditado) {
      alert('Por favor, carregue ambos os arquivos (bruto e editado)');
      return;
    }

    setIsValidating(true);
    
    try {
      const resultado = validarArquivos(arquivoBruto.conteudo, arquivoEditado.conteudo);
      
      setDivergencias(resultado.divergencias);
      setArquivoCorrigido(resultado.conteudoCorrigido);
      
      if (resultado.divergencias.length === 0) {
        setValidacaoCompleta(true);
        console.log('✅ Validação concluída: Nenhuma divergência encontrada!');
        console.log('🚀 Processando automaticamente o arquivo validado...');
        
        // Processar automaticamente o arquivo editado validado
        setTimeout(() => {
          processCSV(arquivoEditado.conteudo);
        }, 500);
      } else {
        console.log(`⚠️ Validação concluída: ${resultado.divergencias.length} divergências encontradas`);
      }
    } catch (error) {
      console.error('❌ Erro na validação:', error);
      alert('Erro ao validar arquivos. Verifique o console para detalhes.');
    } finally {
      setIsValidating(false);
    }
  };

  // Baixar arquivo corrigido
  const baixarArquivoCorrigido = () => {
    if (!arquivoCorrigido) return;

    const blob = new Blob([arquivoCorrigido], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = arquivoEditado.nome.replace('.csv', '_CORRIGIDO.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log('💾 Arquivo corrigido baixado!');
  };

  // Resetar validação
  const resetarValidacao = () => {
    setArquivoBruto(null);
    setArquivoEditado(null);
    setDivergencias([]);
    setValidacaoCompleta(false);
    setArquivoCorrigido(null);
  };

  // ==================== PARTE 2: PROCESSAMENTO ====================
  
  // Função para extrair competência do CSV (MMAAAA)
  const extrairCompetencia = () => {
    if (!csvData.length) {
      return '012025'; // Fallback
    }
    
    // Pegar a primeira transação para extrair mês/ano
    // Data vem no formato DDMMYYYY (ex: 06012025)
    const primeiraData = csvData[0].data;
    if (primeiraData.length === 8) {
      const mes = primeiraData.substring(2, 4);
      const ano = primeiraData.substring(4, 8);
      return `${mes}${ano}`;
    }
    
    return '012025'; // Fallback
  };

  // Função para formatar data para o formato DDMMYYYY
  const formatarDataCSV = (dataStr) => {
    // dataStr vem no formato DDMMYYYY ou DD/MM/YYYY
    const cleaned = dataStr.replace(/\//g, '');
    if (cleaned.length === 8) {
      return cleaned; // Já está no formato DDMMYYYY
    }
    return dataStr;
  };

  // Função para remover acentos e converter para maiúsculo
  const removerAcentosEMaiusculo = (texto) => {
    // Mapa de substituição de caracteres acentuados
    const mapaAcentos = {
      'á': 'a', 'à': 'a', 'ã': 'a', 'â': 'a', 'ä': 'a',
      'é': 'e', 'è': 'e', 'ê': 'e', 'ë': 'e',
      'í': 'i', 'ì': 'i', 'î': 'i', 'ï': 'i',
      'ó': 'o', 'ò': 'o', 'õ': 'o', 'ô': 'o', 'ö': 'o',
      'ú': 'u', 'ù': 'u', 'û': 'u', 'ü': 'u',
      'ç': 'c',
      'ñ': 'n',
      'Á': 'A', 'À': 'A', 'Ã': 'A', 'Â': 'A', 'Ä': 'A',
      'É': 'E', 'È': 'E', 'Ê': 'E', 'Ë': 'E',
      'Í': 'I', 'Ì': 'I', 'Î': 'I', 'Ï': 'I',
      'Ó': 'O', 'Ò': 'O', 'Õ': 'O', 'Ô': 'O', 'Ö': 'O',
      'Ú': 'U', 'Ù': 'U', 'Û': 'U', 'Ü': 'U',
      'Ç': 'C',
      'Ñ': 'N'
    };
    
    // Substituir caracteres acentuados usando o mapa
    let resultado = texto;
    Object.keys(mapaAcentos).forEach(char => {
      resultado = resultado.replace(new RegExp(char, 'g'), mapaAcentos[char]);
    });
    
    // Tentar também com normalize como fallback
    resultado = resultado
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    
    // LIMPEZA AGRESSIVA: Remover qualquer caractere que não seja ASCII imprimível
    // Mantém apenas letras, números, espaços e caracteres básicos de pontuação
    resultado = resultado.replace(/[^\x20-\x7E]/g, '');
    
    return resultado.toUpperCase();
  };

  // Função para remover vírgulas
  const removerVirgulas = (str) => {
    return str.replace(/,/g, '');
  };

  // Função para formatar moeda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para processar o CSV
  const processCSV = (content) => {
    console.log('🔄 Iniciando processamento do CSV do Banco do Brasil...');
    console.log(`📄 Total de linhas no arquivo: ${content.split('\n').length}`);
    
    const lines = content.split('\n').filter(line => line.trim());
    console.log(`📄 Linhas após filtro (não vazias): ${lines.length}\n`);
    
    const data = [];
    
    let totalRecebimentos = 0;
    let valorTotalRecebimentos = 0;
    let totalPagamentos = 0;
    let valorTotalPagamentos = 0;
    let totalTransacoes = 0;
    let saldoInicial = 0;
    let linhasIgnoradas = 0;

    lines.forEach((line, index) => {
      const columns = line.split(';');
      
      if (columns.length < 12) {
        console.log(`⚠️ Linha ${index + 1} ignorada (colunas insuficientes)`);
        return;
      }

      // Extrair dados das colunas
      const codigo = columns[8]?.trim() || '';
      const data_movimento = columns[3]?.trim() || '';
      const historico = columns[9]?.trim() || '';
      const valor_str = columns[10]?.trim() || '';
      const tipo = columns[11]?.trim() || ''; // D ou C
      const complemento = columns[12]?.trim() || '';
      const categoria = columns[13]?.trim() || ''; // Coluna adicional com categoria (PAGAMENTO FUNCIONÁRIO, etc)

      // Ignorar linha de saldo final informativo (código 999 ou histórico "S A L D O")
      if (codigo === '999' || historico.includes('S A L D O')) {
        linhasIgnoradas++;
        console.log(`⚠️ Linha ${index + 1} IGNORADA (Saldo Final Informativo): ${historico} | Valor: ${valor_str}`);
        return; // Pula esta linha
      }

      // Verificar se é saldo anterior
      if (historico.includes('Saldo Anterior')) {
        // Extrair o saldo inicial do valor
        const saldoMatch = valor_str.match(/[\d,.]+/);
        if (saldoMatch) {
          const valorAbsoluto = parseFloat(saldoMatch[0].replace(/\./g, '').replace(',', '.'));
          // Se tipo for D (débito), o saldo é negativo (cheque especial)
          // Se tipo for C (crédito), o saldo é positivo
          saldoInicial = tipo === 'D' ? -valorAbsoluto : valorAbsoluto;
          console.log(`💰 Saldo Inicial Detectado: R$ ${saldoInicial.toFixed(2)} (${tipo === 'D' ? 'NEGATIVO - Cheque Especial' : 'POSITIVO'})`);
        }
        return; // Pula para a próxima linha
      }

      // Parsear o valor
      const valorMatch = valor_str.match(/R\$\s*([\d.,]+)/);
      if (!valorMatch) {
        return; // Ignora linhas sem valor válido
      }
      
      const valor = parseFloat(valorMatch[1].replace(/\./g, '').replace(',', '.'));

      // Determinar se é recebimento ou pagamento
      const isRecebimento = tipo === 'C';
      const isPagamento = tipo === 'D';

      // Montar descrição completa
      const descricao = `${historico} - ${complemento}`.trim();

      // Verificar se é recebimento TOV SOLUCOE ou RJX SERVICO
      const isTovSolucoes = isRecebimento && complemento.toUpperCase().includes('TOV SOLUCOE');
      const isRjxServico = isRecebimento && complemento.toUpperCase().includes('RJX SERVICO');

      const row = {
        data: data_movimento,
        historico,
        complemento,
        categoria,
        descricao,
        valor,
        tipo,
        isRecebimento,
        isPagamento,
        isTovSolucoes,
        isRjxServico
      };

      data.push(row);
      totalTransacoes++;

      // Contar recebimentos
      if (isRecebimento) {
        totalRecebimentos++;
        valorTotalRecebimentos += valor;
        console.log(`💚 RECEBIMENTO #${totalRecebimentos}: ${historico} | Valor: R$ ${valor.toFixed(2)} | Acum: R$ ${valorTotalRecebimentos.toFixed(2)}`);
        if (isTovSolucoes) {
          console.log(`   ✅ É TOV SOLUCOES: ${complemento}`);
        }
        if (isRjxServico) {
          console.log(`   ✅ É RJX SERVICO: ${complemento}`);
        }
      }

      // Contar pagamentos
      if (isPagamento) {
        totalPagamentos++;
        valorTotalPagamentos += valor;
        console.log(`🔴 PAGAMENTO #${totalPagamentos}: ${historico} | Valor: R$ ${valor.toFixed(2)} | Acum: R$ ${valorTotalPagamentos.toFixed(2)}`);
        console.log(`   📝 Complemento: ${complemento}`);
      }
    });

    console.log('\n📊 ============= RESUMO DO PROCESSAMENTO =============');
    console.log(`   📋 Total de linhas lidas: ${lines.length}`);
    console.log(`   ⚠️  Linhas ignoradas (Saldo Final): ${linhasIgnoradas}`);
    console.log(`   ✅ Transações processadas: ${totalTransacoes}`);
    console.log(`\n💚 RECEBIMENTOS:`);
    console.log(`   Quantidade: ${totalRecebimentos} transações`);
    console.log(`   Valor Total: R$ ${valorTotalRecebimentos.toFixed(2)}`);
    console.log(`\n🔴 PAGAMENTOS:`);
    console.log(`   Quantidade: ${totalPagamentos} transações`);
    console.log(`   Valor Total: R$ ${valorTotalPagamentos.toFixed(2)}`);
    console.log(`\n💰 CÁLCULO DO SALDO:`);
    console.log(`   (1) Saldo Inicial: R$ ${saldoInicial.toFixed(2)}`);
    console.log(`   (2) + Recebimentos: R$ ${valorTotalRecebimentos.toFixed(2)}`);
    console.log(`   (3) - Pagamentos: R$ ${valorTotalPagamentos.toFixed(2)}`);
    console.log(`   ────────────────────────────────────────`);
    console.log(`   RESULTADO: R$ ${(saldoInicial + valorTotalRecebimentos - valorTotalPagamentos).toFixed(2)}`);

    const saldoFinal = saldoInicial + valorTotalRecebimentos - valorTotalPagamentos;
    console.log(`\n✅ Saldo Final Calculado: R$ ${saldoFinal.toFixed(2)}`);
    console.log('====================================================\n');

    setCsvData(data);
    setSummary({
      totalTransacoes,
      totalRecebimentos,
      valorTotalRecebimentos,
      totalPagamentos,
      valorTotalPagamentos,
      saldoInicial,
      saldoFinal
    });
  };

  // Função para lidar com o upload do arquivo
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsProcessing(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result;
        processCSV(content);
        setIsProcessing(false);
      };
      reader.readAsText(file); // O browser detectará automaticamente o encoding
    }
  };

  // Funções de drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setIsProcessing(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result;
        processCSV(content);
        setIsProcessing(false);
      };
      reader.readAsText(file); // O browser detectará automaticamente o encoding
    }
  };

  // Função para gerar o CSV formatado
  const gerarCSVFormatado = () => {
    console.log('📥 Gerando CSV formatado para Questor...');
    
    const linhasCSV = [];
    
    // Separar recebimentos e pagamentos
    const transacoesRecebimento = csvData.filter(row => row.isRecebimento);
    const transacoesPagamento = csvData.filter(row => row.isPagamento);

    // Processar recebimentos
    transacoesRecebimento.forEach((row, index) => {
      const dataFormatada = formatarDataCSV(row.data);
      
      // Determinar contas com base na regra
      let contaDebito, contaCredito;
      if (row.isTovSolucoes) {
        contaDebito = 10;
        contaCredito = 8;
      } else if (row.isRjxServico) {
        contaDebito = 10;
        contaCredito = 21;
      } else {
        // Outros recebimentos (por enquanto mesmo padrão)
        contaDebito = 10;
        contaCredito = 8;
      }
      
      const valor = row.valor.toFixed(2);
      const zero = 0;
      
      // Processar descrição
      const descricaoOriginal = row.descricao.trim();
      const descricaoSemAcento = removerAcentosEMaiusculo(descricaoOriginal);
      const descricaoFinal = removerVirgulas(descricaoSemAcento);
      const descricao = `"${descricaoFinal}"`;

      const linha = `${dataFormatada},${contaDebito},${contaCredito},${valor},${zero},${descricao}`;
      linhasCSV.push(linha);
      
      if (index < 5) {
        const tipo = row.isTovSolucoes ? 'TOV SOLUCOES' : row.isRjxServico ? 'RJX SERVICO' : 'OUTRO';
        console.log(`📄 RECEBIMENTO ${index + 1}: Débito=${contaDebito}, Crédito=${contaCredito}, Valor=${valor}, Tipo=${tipo}`);
      }
    });

    // Processar pagamentos
    transacoesPagamento.forEach((row, index) => {
      const dataFormatada = formatarDataCSV(row.data);
      const contaCredito = 10;
      const valor = row.valor.toFixed(2);
      const zero = 0;
      
      // Processar descrição
      const descricaoOriginal = row.descricao.trim();
      const descricaoSemAcento = removerAcentosEMaiusculo(descricaoOriginal);
      const descricaoFinal = removerVirgulas(descricaoSemAcento);
      const descricao = `"${descricaoFinal}"`;
      
      // Processar histórico, complemento e categoria para identificação
      const historicoLimpo = removerAcentosEMaiusculo(row.historico.trim());
      const complementoLimpo = removerAcentosEMaiusculo(row.complemento.trim());
      const categoriaLimpa = removerAcentosEMaiusculo(row.categoria?.trim() || '');
      const textoCompleto = `${historicoLimpo} ${complementoLimpo} ${categoriaLimpa}`.toUpperCase();
      
      // Determinar conta débito com base na categoria, descrição, histórico e complemento
      let contaDebito = 0; // Padrão para outros pagamentos
      
      // Primeira prioridade: verificar categoria diretamente
      if (categoriaLimpa.includes('IOF') || categoriaLimpa.includes('TARIFA')) {
        contaDebito = 4698;
      } else if (categoriaLimpa.includes('FUNCION')) {
        // Aceita tanto "FUNCIONARIO" quanto "FUNCIONRIO" (com problema de encoding)
        contaDebito = 4867;
      } else if (categoriaLimpa.includes('CONVENIO') || complementoLimpo.includes('NOTRE DAME')) {
        contaDebito = 4876;
      } else if (categoriaLimpa.includes('SEGURO') && categoriaLimpa.includes('VIDA')) {
        contaDebito = 4877;
      } else if (categoriaLimpa.includes('TICKET') && categoriaLimpa.includes('PLUS')) {
        contaDebito = 4868;
      } else if (categoriaLimpa.includes('ADVOGADO') || complementoLimpo.includes('ADVOGADO')) {
        contaDebito = 4872;
      } else if (categoriaLimpa.includes('IMPOSTO') || complementoLimpo.includes('RECEITA FEDERAL') || historicoLimpo.includes('DARF')) {
        contaDebito = 4656;
      }
      // Segunda prioridade: verificar histórico se categoria não identificou
      else if (textoCompleto.includes('IOF') || textoCompleto.includes('TARIFA')) {
        contaDebito = 4698;
      }

      const linha = `${dataFormatada},${contaDebito},${contaCredito},${valor},${zero},${descricao}`;
      linhasCSV.push(linha);
      
      if (index < 10) {
        console.log(`📄 PAGAMENTO ${index + 1}: Débito=${contaDebito}, Crédito=${contaCredito}, Valor=${valor}`);
        console.log(`   Categoria RAW: "${row.categoria}"`);
        console.log(`   Categoria LIMPA: "${categoriaLimpa}"`);
        console.log(`   Histórico: "${historicoLimpo.substring(0, 30)}"`);
      }
    });

    const csvContent = linhasCSV.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `BANCO DO BRASIL RJX LANCAMENTOS CSV QUESTOR ${extrairCompetencia()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log(`✅ CSV gerado com ${linhasCSV.length} linhas`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Cabeçalho */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to={createPageUrl("Operations")}>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Voltar para Operações
              </Button>
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-100 rounded-xl">
              <CreditCard className="w-8 h-8 text-yellow-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">BANCO DO BRASIL - RJX</h1>
              <p className="text-gray-600 mt-1">Gerenciamento de operações contábeis Banco do Brasil</p>
            </div>
          </div>
        </div>

        {/* ==================== PARTE 1: VALIDAÇÃO ==================== */}
        <Card className="mb-8 border-2 border-blue-300 bg-blue-50/30">
          <CardHeader className="bg-blue-100/50">
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <Copy className="w-5 h-5" />
              PARTE 1: Validação de Arquivos
            </CardTitle>
            <p className="text-sm text-blue-700 mt-2">
              Compare o arquivo bruto do Banco do Brasil com o arquivo editado para garantir que todos os valores estejam corretos
            </p>
          </CardHeader>
          <CardContent>
            {/* Upload dos arquivos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Arquivo Bruto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Arquivo Bruto (BB-Extrato*.csv)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputBrutoRef.current?.click()}
                    className="mb-2"
                  >
                    Selecionar Arquivo Bruto
                  </Button>
                  {arquivoBruto && (
                    <p className="text-sm text-green-600 mt-2">✓ {arquivoBruto.nome}</p>
                  )}
                  <input
                    ref={fileInputBrutoRef}
                    type="file"
                    accept=".csv"
                    onChange={(e) => e.target.files?.[0] && handleArquivoBruto(e.target.files[0])}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Arquivo Editado */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Arquivo Editado (com descrições)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputEditadoRef.current?.click()}
                    className="mb-2"
                  >
                    Selecionar Arquivo Editado
                  </Button>
                  {arquivoEditado && (
                    <p className="text-sm text-green-600 mt-2">✓ {arquivoEditado.nome}</p>
                  )}
                  <input
                    ref={fileInputEditadoRef}
                    type="file"
                    accept=".csv"
                    onChange={(e) => e.target.files?.[0] && handleArquivoEditado(e.target.files[0])}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            {/* Botões de ação */}
            <div className="flex gap-4 mb-6">
              <Button
                onClick={executarValidacao}
                disabled={!arquivoBruto || !arquivoEditado || isValidating}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isValidating ? 'Validando...' : 'Validar Arquivos'}
              </Button>
              {divergencias.length > 0 && (
                <Button
                  onClick={resetarValidacao}
                  variant="outline"
                >
                  Reiniciar Validação
                </Button>
              )}
            </div>

            {/* Resultado da Validação */}
            {divergencias.length === 0 && arquivoCorrigido !== null && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 border-2 border-green-300 rounded-lg p-6 mb-6"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-green-900 mb-2">✅ Validação Concluída com Sucesso!</h3>
                    <p className="text-green-700">
                      Os valores do arquivo editado correspondem 100% aos valores do arquivo bruto.
                    </p>
                    <p className="text-green-700 mt-2 font-medium">
                      Você pode prosseguir para a Parte 2 (Processamento).
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Divergências Encontradas */}
            {divergencias.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border-2 border-red-300 rounded-lg p-6 mb-6"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 bg-red-100 rounded-full">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-red-900 mb-2">
                      ⚠️ {divergencias.length} Divergência(s) Encontrada(s)
                    </h3>
                    <p className="text-red-700 mb-4">
                      Os seguintes lançamentos possuem valores diferentes entre o arquivo bruto e editado:
                    </p>

                    {/* Tabela de Divergências */}
                    <div className="overflow-x-auto bg-white rounded-lg border border-red-200">
                      <table className="w-full text-sm">
                        <thead className="bg-red-100">
                          <tr>
                            <th className="text-left p-3 font-medium text-red-900">Linha</th>
                            <th className="text-left p-3 font-medium text-red-900">Data</th>
                            <th className="text-left p-3 font-medium text-red-900">Histórico</th>
                            <th className="text-right p-3 font-medium text-red-900">Valor Bruto</th>
                            <th className="text-right p-3 font-medium text-red-900">Valor Editado</th>
                            <th className="text-right p-3 font-medium text-red-900">Diferença</th>
                          </tr>
                        </thead>
                        <tbody>
                          {divergencias.map((div, index) => (
                            <tr key={index} className="border-b border-red-100">
                              <td className="p-3">{div.linha}</td>
                              <td className="p-3">{div.data}</td>
                              <td className="p-3 max-w-xs truncate">{div.historico}</td>
                              <td className="p-3 text-right font-medium text-green-700">
                                R$ {div.valorBruto.toFixed(2)}
                              </td>
                              <td className="p-3 text-right font-medium text-red-700">
                                R$ {div.valorEditado.toFixed(2)}
                              </td>
                              <td className="p-3 text-right font-bold text-red-900">
                                {div.diferenca > 0 ? '+' : ''}R$ {div.diferenca.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Botão para baixar arquivo corrigido */}
                    <div className="mt-4">
                      <Button
                        onClick={baixarArquivoCorrigido}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Baixar Arquivo Editado Corrigido
                      </Button>
                      <p className="text-sm text-red-700 mt-2">
                        O arquivo corrigido terá os valores ajustados para corresponder exatamente ao arquivo bruto.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>

        {/* ==================== PARTE 2: PROCESSAMENTO ==================== */}
        <Card className={`mb-8 border-2 ${validacaoCompleta ? 'border-yellow-300 bg-yellow-50/30' : 'border-gray-200 bg-gray-50/30 opacity-60'}`}>
          <CardHeader className={validacaoCompleta ? 'bg-yellow-100/50' : 'bg-gray-100/50'}>
            <CardTitle className={`flex items-center gap-2 ${validacaoCompleta ? 'text-yellow-900' : 'text-gray-500'}`}>
              <FileText className="w-5 h-5" />
              PARTE 2: Processamento para Questor
            </CardTitle>
            <p className={`text-sm mt-2 ${validacaoCompleta ? 'text-yellow-700' : 'text-gray-500'}`}>
              {validacaoCompleta 
                ? summary 
                  ? 'Arquivo processado com sucesso! Você pode fazer o upload de outro arquivo se necessário'
                  : 'Processando arquivo validado automaticamente...'
                : 'Complete a validação na Parte 1 antes de processar o arquivo'}
            </p>
          </CardHeader>
          <CardContent>
            {!validacaoCompleta && (
              <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-8 text-center">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-gray-600 font-medium">
                  Esta seção estará disponível após a validação dos arquivos
                </p>
              </div>
            )}

            {validacaoCompleta && !summary && (
              <div className="bg-yellow-100 border-2 border-yellow-300 rounded-lg p-8 text-center">
                <div className="animate-spin w-12 h-12 border-4 border-yellow-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-yellow-800 font-medium text-lg">
                  Processando arquivo validado...
                </p>
                <p className="text-yellow-700 text-sm mt-2">
                  Aguarde enquanto geramos o resumo financeiro
                </p>
              </div>
            )}

            {validacaoCompleta && summary && (
              <>
                <div className="bg-green-100 border-2 border-green-300 rounded-lg p-6 mb-6 text-center">
                  <svg className="w-12 h-12 text-green-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-green-800 font-semibold text-lg">
                    Processamento Concluído!
                  </p>
                  <p className="text-green-700 text-sm mt-1">
                    Arquivo processado: <strong>{arquivoEditado?.nome}</strong>
                  </p>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <p className="text-sm text-gray-600 text-center mb-3">
                    Deseja processar outro arquivo? (Opcional)
                  </p>
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                      isDragOver 
                        ? 'border-yellow-500 bg-yellow-50' 
                        : 'border-gray-300 hover:border-yellow-400'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="flex flex-col items-center gap-3">
                      <Upload className="w-6 h-6 text-gray-400" />
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex items-center gap-2"
                        disabled={isProcessing}
                      >
                        <Upload className="w-4 h-4" />
                        Selecionar Outro Arquivo
                      </Button>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Resumo Financeiro */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-indigo-50 border-indigo-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-indigo-600 text-sm font-medium">Saldo Inicial</p>
                    <p className="text-2xl font-bold text-indigo-700">
                      {formatCurrency(summary.saldoInicial)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-blue-600 text-sm font-medium">Valor Recebimentos</p>
                    <p className="text-2xl font-bold text-blue-700">
                      {formatCurrency(summary.valorTotalRecebimentos)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-red-600 text-sm font-medium">Valor Pagamentos</p>
                    <p className="text-2xl font-bold text-red-700">
                      {formatCurrency(summary.valorTotalPagamentos)}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-6">
                  <div>
                    <p className="text-green-600 text-sm font-medium">Saldo Final</p>
                    <p className="text-2xl font-bold text-green-700">
                      {formatCurrency(summary.saldoFinal)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Botão de Download */}
            <div className="flex gap-4 mb-8">
              <Button 
                onClick={gerarCSVFormatado} 
                className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700"
              >
                <Download className="w-4 h-4" />
                Baixar CSV Formatado para Questor
              </Button>
            </div>

            {/* Tabela de Transações */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Transações Processadas ({csvData.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-3 font-medium text-gray-700">Data</th>
                        <th className="text-left p-3 font-medium text-gray-700">Tipo</th>
                        <th className="text-left p-3 font-medium text-gray-700">Histórico</th>
                        <th className="text-left p-3 font-medium text-gray-700">Complemento</th>
                        <th className="text-right p-3 font-medium text-gray-700">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.map((row, index) => {
                        // Limpar descrições para exibição (remover acentos e caracteres especiais)
                        const historicoLimpo = removerAcentosEMaiusculo(row.historico.trim());
                        const complementoLimpo = removerAcentosEMaiusculo(row.complemento.trim());
                        
                        return (
                          <tr key={index} className="border-b hover:bg-gray-50">
                            <td className="p-3">{row.data}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                row.isRecebimento ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {row.isRecebimento ? 'Recebimento' : 'Pagamento'}
                              </span>
                            </td>
                            <td className="p-3 max-w-xs truncate">{historicoLimpo}</td>
                            <td className="p-3 max-w-xs truncate">{complementoLimpo}</td>
                            <td className="p-3 text-right font-medium">{formatCurrency(row.valor)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
